import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BriefcaseBusiness,
  Check,
  ExternalLink,
  Filter,
  LoaderCircle,
  MapPin,
  Search,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";

const initialFilters = {
  q: "",
  city: "",
  specialty: "",
  language: "",
  minExperience: "",
  requiredRadiusKm: "",
  maxStartingPrice: "",
  availableOnly: true,
};

function money(range) {
  if (!range || range.minimum === undefined) return "Pricing on request";
  const currency = range.currency || "INR";
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  const minimum = formatter.format(range.minimum);
  return range.maximum === undefined
    ? `From ${minimum}`
    : `${minimum} – ${formatter.format(range.maximum)}`;
}

function dateTime(value) {
  if (!value) return "Schedule not set";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function PartnerDirectory({ token, events, eventTypes, loadDirectory, assign }) {
  const assignableEvents = useMemo(
    () => events.filter((event) => !["expired", "archived"].includes(event.status)),
    [events],
  );
  const [eventId, setEventId] = useState(assignableEvents[0]?.id || "");
  const [filters, setFilters] = useState(initialFilters);
  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const selectedEvent = assignableEvents.find((event) => event.id === eventId);

  useEffect(() => { setPage(1); }, [eventId, filters]);

  useEffect(() => {
    if (!eventId && assignableEvents[0]) setEventId(assignableEvents[0].id);
  }, [assignableEvents, eventId]);

  useEffect(() => {
    setSelectedIds(
      (selectedEvent?.assignedPhotographers || []).map((photographer) => photographer.id),
    );
    setReason("");
  }, [selectedEvent]);

  useEffect(() => {
    if (!eventId) {
      setProfiles([]);
      setPagination({ page: 1, pages: 0, total: 0 });
      return undefined;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result = await loadDirectory(
          token,
          { eventId, ...filters, page, limit: 24 },
          controller.signal,
        );
        setProfiles(result.profiles || []);
        setPagination(result.pagination || { page, pages: 0, total: 0 });
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Unable to load approved partners");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [eventId, filters, page, loadDirectory, token]);

  const currentIds = useMemo(
    () => new Set((selectedEvent?.assignedPhotographers || []).map((item) => item.id)),
    [selectedEvent],
  );
  const chosenIds = useMemo(() => new Set(selectedIds), [selectedIds]);
  const added = selectedIds.filter((id) => !currentIds.has(id));
  const removed = [...currentIds].filter((id) => !chosenIds.has(id));
  const changed = added.length > 0 || removed.length > 0;
  const assignedLabels = (selectedEvent?.assignedPhotographers || [])
    .filter((person) => chosenIds.has(person.id));

  function updateFilter(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
  }

  function toggle(profile) {
    if (!profile.availability?.available && !selectedIds.includes(profile.user.id)) return;
    setSelectedIds((current) =>
      current.includes(profile.user.id)
        ? current.filter((id) => id !== profile.user.id)
        : [...current, profile.user.id],
    );
  }

  async function confirmAssignment() {
    if (saving) return;
    setSaving(true);
    let saved = false;
    try { saved = await assign(selectedEvent, selectedIds, reason.trim()); }
    finally { setSaving(false); }
    if (saved) {
      setConfirming(false);
      setReason("");
    }
  }

  return (
    <section className="directory-workspace">
      <header className="directory-heading">
        <div>
          <p className="eyebrow">Approved partner network</p>
          <h1>Find and assign the right photographer</h1>
          <p>
            Match approved partners to an event, verify workload and availability,
            then record a reasoned assignment decision.
          </p>
        </div>
        <span className="directory-trust"><ShieldCheck size={17} /> Approved profiles only</span>
      </header>

      {!assignableEvents.length ? (
        <div className="admin-panel directory-empty">
          <BriefcaseBusiness size={28} />
          <h2>Create an active event first</h2>
          <p>The directory becomes available when an event can receive a partner team.</p>
        </div>
      ) : (
        <>
          <div className="admin-panel directory-context">
            <label className="directory-event-select">
              Event to staff
              <select value={eventId} onChange={(event) => setEventId(event.target.value)}>
                {assignableEvents.map((event) => (
                  <option value={event.id} key={event.id}>
                    {event.name} · {dateTime(event.startsAt)}
                  </option>
                ))}
              </select>
            </label>
            <div className="directory-event-facts">
              <span><small>Event type</small><strong>{selectedEvent?.eventType?.name || "Unclassified"}</strong></span>
              <span><small>Venue</small><strong>{selectedEvent?.venue?.city || selectedEvent?.venue?.name || "Not set"}</strong></span>
              <span><small>Current team</small><strong>{currentIds.size} partner{currentIds.size === 1 ? "" : "s"}</strong></span>
            </div>
          </div>

          <div className="admin-panel directory-filters">
            <div className="directory-filter-title"><Filter size={16} /><strong>Match criteria</strong></div>
            <label className="directory-search"><Search size={16} /><input value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} placeholder="Name, business or specialty" /></label>
            <label>City<input value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} placeholder="e.g. Pune" /></label>
            <label>Specialty<input value={filters.specialty} onChange={(event) => updateFilter("specialty", event.target.value)} placeholder="e.g. wedding" list="directory-specialties" /></label>
            <datalist id="directory-specialties">
              {[...new Set(eventTypes.flatMap((type) => type.recommendedSpecialtyCodes || []))].map((code) => <option value={code} key={code} />)}
            </datalist>
            <label>Language<input value={filters.language} onChange={(event) => updateFilter("language", event.target.value)} placeholder="e.g. Marathi" /></label>
            <label>Min. experience<input type="number" min="0" max="80" value={filters.minExperience} onChange={(event) => updateFilter("minExperience", event.target.value)} placeholder="Years" /></label>
            <label>Required radius<input type="number" min="0" value={filters.requiredRadiusKm} onChange={(event) => updateFilter("requiredRadiusKm", event.target.value)} placeholder="Kilometres" /></label>
            <label>Max starting price<input type="number" min="0" value={filters.maxStartingPrice} onChange={(event) => updateFilter("maxStartingPrice", event.target.value)} placeholder="INR" /></label>
            <label className="directory-checkbox"><input type="checkbox" checked={filters.availableOnly} onChange={(event) => updateFilter("availableOnly", event.target.checked)} /> Available for this schedule</label>
          </div>

          <div className="directory-results-bar">
            <div><strong>{pagination.total}</strong> matching approved partner{pagination.total === 1 ? "" : "s"} · page {pagination.page} of {Math.max(pagination.pages, 1)}</div>
            {loading && <span><LoaderCircle className="spin" size={15} /> Checking live availability…</span>}
          </div>
          {error && <div className="admin-alert"><AlertTriangle size={16} /> {error}</div>}

          <div className="directory-grid">
            {profiles.map((profile) => {
              const selected = selectedIds.includes(profile.user.id);
              const available = profile.availability?.available;
              return (
                <article className={`admin-panel directory-card ${selected ? "selected" : ""}`} key={profile.id}>
                  <header>
                    <div className="directory-avatar">{(profile.user.displayName || profile.businessName || "P").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</div>
                    <div><p className="eyebrow">{profile.businessName || "Independent partner"}</p><h2>{profile.user.displayName || profile.user.email}</h2><span><MapPin size={13} /> {profile.primaryCity || "Location not supplied"}</span></div>
                    <button className={`directory-select ${selected ? "active" : ""}`} disabled={!available && !selected} onClick={() => toggle(profile)} aria-label={`${selected ? "Remove" : "Select"} ${profile.user.displayName}`}>
                      {selected ? <Check size={17} /> : <Users size={17} />}
                    </button>
                  </header>
                  <div className="directory-metrics">
                    <span><small>Experience</small><strong>{profile.experienceYears ?? 0} years</strong></span>
                    <span><small>Upcoming load</small><strong>{profile.currentWorkload} event{profile.currentWorkload === 1 ? "" : "s"}</strong></span>
                    <span><small>Service radius</small><strong>{profile.serviceRadiusKm ?? 0} km</strong></span>
                    <span><small>Pricing</small><strong>{money(profile.pricingRange)}</strong></span>
                  </div>
                  <div className="partner-specialties">{(profile.specialtyCodes || []).map((code) => <span key={code}>{code.replaceAll("_", " ")}</span>)}</div>
                  <p className={`directory-availability ${available ? "available" : "unavailable"}`}>{available ? "Available for this event" : `Conflict: ${profile.availability.conflicts.map((item) => item.name).join(", ")}`}</p>
                  <footer>
                    <span>{(profile.languages || []).join(" · ") || "Languages not supplied"}</span>
                    {profile.portfolioUrls?.[0] && <a href={profile.portfolioUrls[0]} target="_blank" rel="noreferrer">Portfolio <ExternalLink size={13} /></a>}
                  </footer>
                </article>
              );
            })}
          </div>

          <nav className="directory-pages" aria-label="Partner directory pages">
            <button className="button button-light" disabled={loading || page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
            <span>Page {page} / {Math.max(1, pagination.pages)}</span>
            <button className="button button-light" disabled={loading || page >= pagination.pages} onClick={() => setPage((current) => current + 1)}>Next</button>
          </nav>
          <div className="admin-panel directory-assignment-bar">
            <div><small>Proposed team</small><strong>{selectedIds.length} selected</strong><p>{added.length} added · {removed.length} removed</p></div>
            <div className="directory-current-team" aria-label="Current partner selection">
              {assignedLabels.map((person) => <button key={person.id} type="button" onClick={() => setSelectedIds((ids) => ids.filter((id) => id !== person.id))} title="Remove from proposed team">{person.displayName || person.email} <X size={13} /></button>)}
            </div>
            <label>Decision reason<textarea value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Why is this partner team right for the event?" /></label>
            <button className="button button-dark" disabled={!changed || !selectedIds.length || reason.trim().length < 3} onClick={() => setConfirming(true)}>Review assignment</button>
          </div>
        </>
      )}

      {confirming && (
        <div className="modal-layer" role="presentation">
          <section className="directory-confirm" role="dialog" aria-modal="true" aria-labelledby="assignment-title">
            <button className="modal-close" onClick={() => setConfirming(false)} aria-label="Close"><X size={18} /></button>
            <p className="eyebrow">Final confirmation</p>
            <h2 id="assignment-title">Update {selectedEvent.name} partner team?</h2>
            <p>This will add {added.length} and remove {removed.length} partner{added.length + removed.length === 1 ? "" : "s"}. A notification and permanent audit entry will be created.</p>
            <blockquote>{reason}</blockquote>
            <div><button className="button button-light" onClick={() => setConfirming(false)}>Go back</button><button className="button button-dark" disabled={saving} onClick={confirmAssignment}>{saving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />} Confirm assignment</button></div>
          </section>
        </div>
      )}
    </section>
  );
}
